import classNames from "classnames";
import React, { FunctionComponent, useCallback, useMemo } from "react";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import { OptionsType } from "react-select";
import { ReactSelect } from "../react-select";

type Prop = {
	className?: string;
	disabled?: boolean;
	initialized?: boolean;
	label?: string;
	multiple?: boolean;
	options: OptionsType<{ label: string; value: string | number }>;
	placeholder?: string;
	required?: boolean;
	searchable?: boolean;
	value?: string | number | string[] | number[];
	validationFunc?: (value: string | number | string[] | number[]) => { valid: boolean; message: string };
	onChange?: (value: string | number | string[] | number[]) => void;
};

export const ValidatableSelect: FunctionComponent<Prop> = React.memo(
	({
		className,
		disabled,
		initialized = true,
		label,
		multiple,
		options,
		placeholder,
		required,
		searchable,
		value,
		validationFunc = () => ({ valid: true, message: "" }),
		onChange: onChange = () => {
			return;
		},
	}) => {
		const { t } = useTranslation();

		const handleUpdateState = useCallback(
			(option: { value: string | number; label: string } | { value: string | number; label: string }[]) => {
				if ("value" in option) return onChange(option.value);

				const values = option.map(({ value }) => value);
				onChange(values as string[] | number[]);
			},
			[onChange]
		);

		const { valid, message } = useMemo(() => {
			if (required && !initialized && (!value || (Array.isArray(value) && !value.length)))
				return { valid: false, message: t("validatable_select.required_field") };
			const { valid, message } = validationFunc(value);
			return { valid, message: valid && required ? t("validatable_select.required_field") : message };
		}, [required, initialized, value, validationFunc]);

		return (
			<div className={classNames("validatable-select", className, { "validatable-select--invalid": !valid })}>
				{label && <Form.Label>{label}</Form.Label>}
				<ReactSelect
					isDisabled={disabled}
					isMulti={multiple}
					isSearchable={searchable}
					options={options}
					onChange={handleUpdateState}
					placeholder={placeholder ? placeholder : t("validatable_select.placeholder")}
					value={value}
				/>
				{!!message && <Form.Text className="validatable-select__annotation">{message}</Form.Text>}
			</div>
		);
	}
);
